import isSessionStorageAvailable from './isSessionStorageAvailable';

/**
 * Sets up the vite:preloadError event handler with cache busting and retry logic
 * This handler prevents infinite reload loops by tracking retry attempts and
 * implements exponential backoff with cache busting parameters
 */
export function setupViteErrorHandler() {
    window.addEventListener('vite:preloadError', event => {
        try {
            const RETRY_KEY = 'sk_vite_preload_retry_count';
            const MAX_ATTEMPTS = 3;
            const BASE_DELAY = 500;

            let retryCount = 0;

            if (isSessionStorageAvailable()) {
                try {
                    retryCount = parseInt(
                        sessionStorage.getItem(RETRY_KEY) || '0',
                        10
                    );
                } catch (error) {
                    console.warn(
                        'Failed to read retry count from sessionStorage:',
                        error
                    );
                }
            }

            retryCount += 1;

            console.error(
                `Vite preload error occurred (attempt ${retryCount}/${MAX_ATTEMPTS}):`,
                event
            );

            if (retryCount >= MAX_ATTEMPTS) {
                if (isSessionStorageAvailable()) {
                    try {
                        sessionStorage.removeItem(RETRY_KEY);
                    } catch (error) {
                        console.warn(
                            'Failed to clear retry count from sessionStorage:',
                            error
                        );
                    }
                }

                if (window.Sentry) {
                    window.Sentry.withScope(scope => {
                        scope.setTag('error_type', 'vite_preload_max_attempts');
                        scope.setContext('preload_error', {
                            attempts: retryCount,
                            url: window.location.href,
                            timestamp: new Date().toISOString()
                        });
                        scope.setLevel('error');
                        window.Sentry.captureMessage(
                            'Vite preload failed after maximum attempts'
                        );
                    });
                }

                if (window.Vue && window.Vue.prototype.$notify) {
                    window.Vue.prototype.$notify({
                        title: 'Loading Error',
                        text: 'There was a problem loading the application. Please refresh the page.',
                        type: 'error',
                        duration: 10000
                    });
                }

                return;
            }

            if (isSessionStorageAvailable()) {
                try {
                    sessionStorage.setItem(RETRY_KEY, retryCount.toString());
                } catch (error) {
                    console.warn(
                        'Failed to store retry count in sessionStorage:',
                        error
                    );
                }
            }

            const delay = BASE_DELAY * retryCount;
            setTimeout(() => {
                const url = new URL(window.location.href);
                url.searchParams.set('_cb', Date.now().toString());
                window.location.href = url.toString();
            }, delay);
        } catch (error) {
            console.error('Error in vite:preloadError handler:', error);
            window.location.reload();
        }
    });
}
